import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AccessLevels, AppStateService, UserAccessService } from '@siza-staff-care/shared/app-state';
import { camelCase } from 'lodash-es';
import { getCurrentSecurityMapping } from '../menu-routes/root-route-to-security-mapping';
import { securityPaths } from '../menu-routes/security-mappings';

export type ControlSecurity = {
	secured?: boolean;
	screenReadOnly?: boolean;
	disable?: boolean;
	hide?: boolean;
	mandatory?: boolean;
};

@Injectable({
	providedIn: 'root',
})
export class ControlSecurityService {
	constructor(
		private router: Router,
		private appState: AppStateService,
		private userAccess: UserAccessService,
	) {}

	private routeOverrideStack: string[] = [];

	private routeOverrideSuspended = false;

	pushRouteOverride(route: string) {
		this.routeOverrideStack.push(route);
	}

	popRouteOverride() {
		this.routeOverrideStack.pop();
	}

	private getRouteOverride() {
		return this.routeOverrideStack[this.routeOverrideStack.length - 1];
	}

	/**
	 * Gets the securityMappings value for the current route. E.g. expenses-query would return ExpensesQuery, which would be
	 * equivalent to securityMappings.ExpensesQuery ( == 'ExpensesQuery' )
	 * @returns
	 */
	getSecurityMappingFromRoute(): string {
		const routeOverride = this.getRouteOverride();
		if (routeOverride && !this.routeOverrideSuspended) {
			return routeOverride;
		}
		return getCurrentSecurityMapping(this.router, this.appState);
	}

	setSuspendRouteOverride(value: boolean) {
		this.routeOverrideSuspended = value;
	}

	/**
	 * Gets the current menu key from the route, via the security mapping.
	 * e.g. staff-records -> StaffMaintenance -> staff.staffMaintenance
	 * @returns
	 */
	getMenuKeyFromRoute(): string {
		const securityKey = this.getSecurityMappingFromRoute();
		const menuKey = securityPaths[securityKey];
		return menuKey;
	}

	/**
	 * Gets a ControlSecurity object for the specified control, using the current route to prepend the screen path.
	 * @param controlKey The key for the individual control.
	 * @returns
	 */
	getControlSecurity(controlKey: string) {
		const securityKey = this.getSecurityMappingFromRoute();
		const menuKey = securityPaths[securityKey] ?? '';
		const combinedKey = `${menuKey}.${controlKey}`;

		const screenAccessLevel = this.userAccess.getAccessLevelForControlPath(menuKey);
		const accessLevel = this.userAccess.getAccessLevelForControlPath(combinedKey);

		const result: ControlSecurity = {
			secured: accessLevel !== AccessLevels.Full,
			screenReadOnly: screenAccessLevel === AccessLevels.ReadOnly,
			disable: accessLevel === AccessLevels.ReadOnly,
			hide: accessLevel === AccessLevels.None,
			mandatory: accessLevel === AccessLevels.Mandatory,
		};

		return result;
	}

	normalisePaths(path: string) {
		if (!path) {
			return path;
		}

		path = path.replace('_', '.');
		let parts = path.split('.');

		// Normalise the parts to camelCase.
		parts = parts.map((p) => camelCase(p));
		return parts.join('.');
	}

	/**
	 * Determines whether the current screen, from the route, is read-only.
	 * @returns
	 */
	isCurrentScreenReadOnly() {
		const path = this.getSecurityMappingFromRoute();
		const screenAccessLevel = this.userAccess.getAccessLevelForControlPath(path);
		const isReadOnly = screenAccessLevel === AccessLevels.ReadOnly;
		return isReadOnly;
	}
}
